// Cálculos de la ficha de la tienda: ranking, benchmarks, equipo, tendencia y clientes. Puro.
import type { SellerKpiRow } from '@/api/services/retail-org/backoffice';
import type { RetailUser } from '@/api/services/retail-org/retail-org.client';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { dayOffset, mxDay, mxDayEndUtc, mxDayStartUtc } from '@/lib/dateHelpers';
import { isSeller } from '../../lib/roster';
import { hasStoreSales, type StoreRow } from './storesTable';

export function median(values: readonly number[]): number | null {
    if (values.length === 0) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

export interface StoreBenchmark {
    value: number | null;
    networkMedian: number | null;
}

export interface StoreBenchmarks {
    sales: StoreBenchmark;
    placed: StoreBenchmark;
    averageTicket: StoreBenchmark;
}

/** La tienda contra la mediana de las tiendas que vendieron en el periodo. */
export function storeBenchmarks(row: StoreRow, rows: readonly StoreRow[]): StoreBenchmarks {
    const sold = rows.filter(hasStoreSales);
    const pick = (field: 'salesCount' | 'placedCents' | 'averageTicketCents'): StoreBenchmark => ({
        value: row[field],
        networkMedian: median(
            sold.map((peer) => peer[field]).filter((value): value is number => value !== null),
        ),
    });
    return {
        sales: pick('salesCount'),
        placed: pick('placedCents'),
        averageTicket: pick('averageTicketCents'),
    };
}

export type VersusTone = 'good' | 'bad' | 'flat';

/** Diferencia contra la mediana de la red; menos de 3% se lee «≈ igual». */
export function versusNetwork(
    value: number | null,
    base: number | null,
): { tone: VersusTone; label: string } {
    if (value === null || base === null || base <= 0) return { tone: 'flat', label: '—' };
    const pct = Math.round(((value - base) / base) * 100);
    if (Math.abs(pct) < 3) return { tone: 'flat', label: '≈ igual' };
    return pct > 0
        ? { tone: 'good', label: `+${String(pct)}% vs red` }
        : { tone: 'bad', label: `${String(pct)}% vs red` };
}

export type TeamMemberStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

export interface TeamMemberRow {
    sellerId: string;
    /** Clave corta legible: `V-` + los últimos 4 caracteres del id. */
    shortCode: string;
    name: string;
    isAdmin: boolean;
    status: TeamMemberStatus | null;
    placedCents: number | null;
    salesCount: number | null;
    goalCents: number | null;
}

export function sellerShortCode(sellerId: string): string {
    return `V-${sellerId.slice(-4).toUpperCase()}`;
}

/**
 * El equipo de la tienda: los vendedores del padrón con ella como casa, activos o no, más quien
 * vendió ahí en el periodo aunque su casa sea otra.
 */
export function buildTeamRows(
    storeId: string,
    managerId: string | null,
    users: readonly RetailUser[],
    kpis: readonly SellerKpiRow[],
): TeamMemberRow[] {
    const kpiById = new Map(kpis.map((row) => [row.sellerId, row]));
    const userById = new Map(users.map((user) => [user.retailUserId, user]));
    const members = users.filter((user) => isSeller(user) && user.homeStoreId === storeId);
    const ids = new Set(members.map((user) => user.retailUserId));
    for (const kpi of kpis) ids.add(kpi.sellerId);

    return [...ids].map((sellerId) => {
        const user = userById.get(sellerId);
        const kpi = kpiById.get(sellerId);
        return {
            sellerId,
            shortCode: sellerShortCode(sellerId),
            name: user?.displayName ?? kpi?.name ?? sellerId,
            isAdmin: managerId !== null && managerId === sellerId,
            status: user ? user.status : null,
            placedCents: kpi ? kpi.soldTodayCents : 0,
            salesCount: kpi ? kpi.unitsToday : 0,
            goalCents: kpi?.dailyGoalCents ?? null,
        };
    });
}

export type TeamSortKey = 'shortCode' | 'name' | 'role' | 'status' | 'placed' | 'sales' | 'goal';

export interface TeamSort {
    key: TeamSortKey;
    direction: 'asc' | 'desc';
}

export const DEFAULT_TEAM_SORT: TeamSort = { key: 'placed', direction: 'desc' };

/** Cumplimiento contra la meta en pesos, como fracción; `null` sin meta. */
export function teamMemberProgress(row: TeamMemberRow): number | null {
    if (row.goalCents === null || row.goalCents <= 0 || row.placedCents === null) return null;
    return row.placedCents / row.goalCents;
}

function teamSortValue(row: TeamMemberRow, key: TeamSortKey): string | number | null {
    switch (key) {
        case 'shortCode':
            return row.shortCode;
        case 'name':
            return row.name;
        case 'role':
            return Number(row.isAdmin);
        case 'status':
            return row.status;
        case 'placed':
            return row.placedCents;
        case 'sales':
            return row.salesCount;
        case 'goal':
            return teamMemberProgress(row);
    }
}

/** Ordena el equipo; los `null` al final y el nombre desempata. */
export function sortTeamRows(rows: readonly TeamMemberRow[], sort: TeamSort): TeamMemberRow[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const av = teamSortValue(a, sort.key);
        const bv = teamSortValue(b, sort.key);
        if (av === null && bv !== null) return 1;
        if (bv === null && av !== null) return -1;
        if (av !== null && bv !== null && av !== bv) {
            const cmp =
                typeof av === 'string' && typeof bv === 'string'
                    ? av.localeCompare(bv, 'es-MX')
                    : Number(av) - Number(bv);
            if (cmp !== 0) return cmp * sign;
        }
        return a.name.localeCompare(b.name, 'es-MX');
    });
}

/** Lugar por colocación dentro del equipo (1 = el mejor); sin colocación no tiene lugar. */
export function teamRanks(rows: readonly TeamMemberRow[]): Map<string, number> {
    const ranked = rows
        .filter((row) => (row.placedCents ?? 0) > 0)
        .sort((a, b) => (b.placedCents ?? 0) - (a.placedCents ?? 0));
    return new Map(ranked.map((row, index) => [row.sellerId, index + 1]));
}

/** Clientes distintos en las ventas no canceladas. */
export function uniqueCustomers(
    sales: readonly Pick<BackofficeSale, 'retailCustomerId' | 'cancelledAt'>[],
): number {
    return new Set(
        sales
            .filter((sale) => sale.cancelledAt === null && sale.retailCustomerId !== null)
            .map((sale) => sale.retailCustomerId),
    ).size;
}

export const TREND_WEEKS = 8;

/** Las ventanas de 7 días de las últimas `weeks` semanas; la 0 es la más reciente. */
export function weeklyWindows(
    weeks: number = TREND_WEEKS,
    now: Date = new Date(),
): { from: string; to: string }[] {
    const today = mxDay(now.toISOString());
    return Array.from({ length: weeks }, (_, index) => {
        const toDay = dayOffset(today, -7 * index);
        return { from: mxDayStartUtc(dayOffset(toDay, -6)), to: mxDayEndUtc(toDay) };
    });
}

/** Cambio de la última semana contra la más vieja de la serie; `null` si la vieja es 0. */
export function trendChangePct(series: readonly number[]): number | null {
    if (series.length < 2) return null;
    const first = series[0];
    const last = series[series.length - 1];
    if (first <= 0) return null;
    return Math.round(((last - first) / first) * 100);
}
